// session.js
// -----------------------------------------------------------------------
// The one in-memory session model for a signed-in browser tab: which
// community is active, who signed in, and when. This is deliberately NOT
// persisted anywhere (no localStorage, no cookie) -- it lives only in React
// state for the lifetime of the tab, same as the rest of useBoardIQ's state.
// A hard refresh (or "sign out", which does a hard navigation -- see
// BoardIQ.jsx) clears it, same as it clears every other in-memory hook.
//
// This replaces the ad hoc `bq.community?.name || "No community"` chains
// that used to be scattered across DesktopApp.jsx/MobileApp.jsx/BoardIQ.jsx,
// and gives every screen one place to read who's signed in and to which
// community, from the moment CommunityPicker's login succeeds -- not just
// once useBoardIQ's own GET /community round-trip resolves.
//
// It's also what makes concurrent communities safe from the *frontend*
// side: startSession() calls api.setActiveCommunityId(), which makes every
// subsequent api.js call carry an `X-Community-Id` header, so the backend's
// _resolve_community_id() no longer has to fall back to the single global
// "last active community" pointer (community.py's get_last_active_community_id)
// for this tab. That pointer remains in place as the safety net for requests
// made before a session exists (or from a tab that bypassed the picker), but
// once a session is started here, this tab is fully self-describing.

import React, { createContext, useCallback, useContext, useMemo, useState } from "react";
import { api } from "./api.js";

const SessionContext = createContext(null);

const EMPTY_SESSION = {
  communityId: null,
  communityName: "",
  userName: "",
  role: null,
  resident: null,
  loginTime: null, // ISO string, set by startSession()
};

export function SessionProvider({ children }) {
  const [session, setSession] = useState(EMPTY_SESSION);

  // Called once, right after POST /communities/select succeeds (see
  // CommunityPicker.jsx's submitLogin) -- before navigating into the app,
  // so BoardIQ.jsx and useBoardIQ() see a populated session on first mount.
  const startSession = useCallback(({ communityId, communityName, userName, role, resident }) => {
    api.setActiveCommunityId(communityId || null);
    setSession({
      communityId: communityId || null,
      communityName: communityName || "",
      userName: userName || "",
      role: role || null,
      resident: resident || null,
      loginTime: new Date().toISOString(),
    });
  }, []);

  const clearSession = useCallback(() => {
    api.setActiveCommunityId(null);
    setSession(EMPTY_SESSION);
  }, []);

  const value = useMemo(() => ({ session, startSession, clearSession }), [session, startSession, clearSession]);
  return React.createElement(SessionContext.Provider, { value }, children);
}

export function useSession() {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error("useSession() must be called inside a <SessionProvider>.");
  return ctx;
}

/** "2:41 PM" for display -- null/invalid input renders as "". */
export function formatLoginTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" });
}
